/* ============================================================
   PINNACLE — PRODUCT.JS
   Product page: gallery, variant selection, bundle upsell
   ============================================================ */

'use strict';

/* ─── Product Gallery ─── */
class ProductGallery {
  constructor(container) {
    this.container    = container;
    this.mainImage    = container.querySelector('.gallery__main-image');
    this.thumbnails   = container.querySelectorAll('.gallery__thumb');
    this.prevBtn      = container.querySelector('.gallery__prev');
    this.nextBtn      = container.querySelector('.gallery__next');
    this.currentIndex = 0;

    if (this.thumbnails.length) this.init();
  }

  init() {
    this.thumbnails.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i));
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.goTo(i);
        }
      });
    });

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    this.setupSwipe();
  }

  goTo(index) {
    const images = Array.from(this.thumbnails).map((t) => t.dataset.fullSrc || t.querySelector('img')?.src);
    const alts   = Array.from(this.thumbnails).map((t) => t.dataset.alt || '');

    if (this.mainImage) {
      this.mainImage.style.opacity = '0';
      this.mainImage.style.transform = 'scale(0.98)';

      setTimeout(() => {
        this.mainImage.src = images[index] || this.mainImage.src;
        this.mainImage.alt = alts[index] || this.mainImage.alt;
        this.mainImage.style.opacity = '1';
        this.mainImage.style.transform = 'scale(1)';
      }, 200);
    }

    this.thumbnails.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-selected', String(i === index));
    });

    this.currentIndex = index;
  }

  prev() {
    const newIndex = (this.currentIndex - 1 + this.thumbnails.length) % this.thumbnails.length;
    this.goTo(newIndex);
  }

  next() {
    const newIndex = (this.currentIndex + 1) % this.thumbnails.length;
    this.goTo(newIndex);
  }

  setupSwipe() {
    let startX = 0;

    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.container.addEventListener('touchend', (e) => {
      const delta = startX - e.changedTouches[0].clientX;
      if (Math.abs(delta) > 50) {
        delta > 0 ? this.next() : this.prev();
      }
    }, { passive: true });
  }
}

/* ─── Variant Selector ─── */
class VariantSelector {
  constructor(form) {
    this.form        = form;
    this.radios      = form.querySelectorAll('[data-variant-radio]');
    this.priceEl     = document.querySelector('[data-product-price]');
    this.addToCartBtn= document.querySelector('[data-add-to-cart]');
    this.variantIdInput = form.querySelector('[name="id"]');

    this.init();
  }

  init() {
    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.onVariantChange(radio));
    });
  }

  onVariantChange(radio) {
    const variantId    = radio.value;
    const price        = radio.dataset.price;
    const available    = radio.dataset.available === 'true';
    const comparePrice = radio.dataset.comparePrice;

    if (this.variantIdInput) {
      this.variantIdInput.value = variantId;
    }

    if (this.priceEl && price) {
      this.priceEl.textContent = this.formatMoney(parseInt(price, 10));
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !available;
      this.addToCartBtn.querySelector('[data-btn-text]').textContent =
        available ? 'Add to Cart' : 'Sold Out';
    }

    const comparePriceEl = document.querySelector('[data-compare-price]');
    if (comparePriceEl) {
      if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
        comparePriceEl.textContent = this.formatMoney(parseInt(comparePrice, 10));
        comparePriceEl.hidden = false;
      } else {
        comparePriceEl.hidden = true;
      }
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
}

/* ─── Quantity Selector ─── */
class QuantitySelector {
  constructor(el) {
    this.el    = el;
    this.input = el.querySelector('[data-qty-input]');
    this.minus = el.querySelector('[data-qty-minus]');
    this.plus  = el.querySelector('[data-qty-plus]');

    if (this.input) this.init();
  }

  init() {
    this.minus?.addEventListener('click', () => this.change(-1));
    this.plus?.addEventListener('click', () => this.change(1));
  }

  change(delta) {
    const current = parseInt(this.input.value, 10) || 1;
    const min     = parseInt(this.input.min, 10) || 1;
    const max     = parseInt(this.input.max, 10) || 99;
    this.input.value = Math.min(max, Math.max(min, current + delta));
    this.input.dispatchEvent(new Event('change'));
  }
}

/* ─── Bundle Upsell ─── */
class BundleUpsell {
  constructor(el) {
    this.el      = el;
    this.options = el.querySelectorAll('[data-bundle-option]');
    this.init();
  }

  init() {
    this.options.forEach((option) => {
      option.addEventListener('click', () => {
        this.options.forEach((o) => o.classList.remove('is-selected'));
        option.classList.add('is-selected');

        const variantId = option.dataset.bundleVariant;
        const hiddenInput = document.querySelector('[name="id"]');
        if (hiddenInput && variantId) {
          hiddenInput.value = variantId;
        }

        const price = option.dataset.bundlePrice;
        const priceEl = document.querySelector('[data-product-price]');
        if (priceEl && price) {
          priceEl.textContent = '$' + (parseInt(price, 10) / 100).toFixed(2);
        }

        this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
        option.setAttribute('aria-selected', 'true');
      });
    });
  }
}

/* ─── Add to Cart (AJAX) ─── */
class AddToCart {
  constructor(form) {
    this.form    = form;
    this.btn     = form.querySelector('[data-add-to-cart]');
    this.btnText = form.querySelector('[data-btn-text]');
    this.init();
  }

  init() {
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.addItem();
    });
  }

  async addItem() {
    if (!this.btn || this.btn.disabled) return;

    this.setLoading(true);

    const formData = new FormData(this.form);
    const data = {
      id:       formData.get('id'),
      quantity: formData.get('quantity') || 1,
    };

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error('Add to cart failed');

      this.setSuccess();
      this.updateCartCount();
      this.openCartDrawer();
    } catch (err) {
      this.setError();
    }
  }

  setLoading(loading) {
    this.btn.disabled = loading;
    if (this.btnText) this.btnText.textContent = loading ? 'Adding...' : 'Add to Cart';
    this.btn.classList.toggle('is-loading', loading);
  }

  setSuccess() {
    if (this.btnText) this.btnText.textContent = 'Added';
    this.btn.classList.add('is-success');
    setTimeout(() => {
      this.btn.classList.remove('is-success');
      if (this.btnText) this.btnText.textContent = 'Add to Cart';
      this.btn.disabled = false;
    }, 2000);
  }

  setError() {
    if (this.btnText) this.btnText.textContent = 'Try Again';
    this.btn.disabled = false;
    this.btn.classList.add('is-error');
    setTimeout(() => {
      this.btn.classList.remove('is-error');
    }, 2000);
  }

  async updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (_) {}
  }

  openCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:open'));
  }
}

/* ─── Tab Switcher (product page tabs) ─── */
class TabSwitcher {
  constructor(el) {
    this.el     = el;
    this.tabs   = el.querySelectorAll('[data-tab]');
    this.panels = el.querySelectorAll('[data-tab-panel]');
    this.init();
  }

  init() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        this.activate(target);
      });
    });
  }

  activate(target) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tab === target;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.tabPanel === target;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });
  }
}

/* ─── Initialize ─── */
document.addEventListener('DOMContentLoaded', () => {
  const gallery = document.querySelector('[data-product-gallery]');
  if (gallery) new ProductGallery(gallery);

  const variantForm = document.querySelector('[data-variant-form]');
  if (variantForm) {
    new VariantSelector(variantForm);
    new AddToCart(variantForm);
  }

  document.querySelectorAll('[data-qty-selector]').forEach((el) => {
    new QuantitySelector(el);
  });

  const bundleUpsell = document.querySelector('[data-bundle-upsell]');
  if (bundleUpsell) new BundleUpsell(bundleUpsell);

  document.querySelectorAll('[data-tabs]').forEach((el) => {
    new TabSwitcher(el);
  });

  if (document.querySelector('[data-product-gallery]')) {
    const mainImg = document.querySelector('.gallery__main-image');
    if (mainImg) {
      mainImg.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    }
  }
});
